import { useState } from 'react'
import { FileText, Folder, Plus, Search, Upload, MoreVertical } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Loading } from '@/components/ui/loading'
import { useFolders, useDocuments } from '@/hooks'

export function DocumentsPage() {
  const [search, setSearch] = useState('')
  const { data: foldersData, isLoading: isFoldersLoading } = useFolders()
  const { data: documentsData, isLoading: isDocumentsLoading } = useDocuments({ search })

  const isLoading = isFoldersLoading || isDocumentsLoading

  const folders = foldersData?.items || []
  const documents = documentsData?.items || []

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <FileText className="w-6 h-6 text-cyan-500" />
              文档管理
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              文件上传、分类管理和权限控制
            </p>
          </div>
        </div>
        <Loading className="py-20" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <FileText className="w-6 h-6 text-cyan-500" />
            文档管理
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            文件上传、分类管理和权限控制
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2">
            <Plus className="w-4 h-4" />
            新建文件夹
          </Button>
          <Button className="gap-2">
            <Upload className="w-4 h-4" />
            上传文件
          </Button>
        </div>
      </div>

      {/* Folders */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {folders.length === 0 ? (
          <div className="col-span-4 text-center py-8 text-slate-500 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
            暂无文件夹
          </div>
        ) : (
          folders.map((folder) => (
            <Card
              key={folder.id}
              className="cursor-pointer hover:border-indigo-500 transition-colors"
            >
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center">
                    <Folder className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">
                      {folder.name}
                    </p>
                    <p className="text-sm text-slate-500">
                      {folder.document_count || folder.count || 0} 个文件
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Search and Files */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="搜索文档..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          <div className="divide-y divide-slate-200 dark:divide-slate-800">
            {documents.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                暂无文档
              </div>
            ) : (
              documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between py-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-slate-500" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">
                        {doc.title || doc.name}
                      </p>
                      <p className="text-sm text-slate-500">
                        {doc.file_size || doc.size} · {doc.updated_at || doc.updatedAt}
                      </p>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon">
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
